import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from './Badge';
import EntityLink from './EntityLink';
import AuditLog from './AuditLog';

const ENGAGEMENT_LABELS = {
  contract_placement: 'Contract Placement',
  recurring_referral: 'Recurring Referral',
  fte_placement: 'FTE Placement',
  business_consulting: 'Business Consulting',
};

export default function WorkOrderPanel({ woId, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Details');

  useEffect(() => {
    api.get(`/work-orders/${woId}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [woId]);

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel" style={{ width: 520 }}>
        <div className="drawer-header">
          <h3 className="drawer-title">{data?.client_wo_number || 'Loading…'}</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {data && <Badge variant="info">{ENGAGEMENT_LABELS[data.engagement_type] || data.engagement_type}</Badge>}
            {data && <Badge variant={data.status === 'active' ? 'success' : 'neutral'}>{data.status}</Badge>}
            <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/work-orders/${woId}`)}>
              View Full Page ↗
            </button>
            <button className="drawer-close" onClick={onClose}>×</button>
          </div>
        </div>

        {!data && (
          <div className="panel-body">
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>Loading…</div>
          </div>
        )}

        {data && (
          <div className="panel-body">
            <div className="panel-tabs" style={{ marginBottom: 16 }}>
              {['Details', 'Purchase Orders'].map(t => (
                <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
                  {t}
                  {t === 'Purchase Orders' && data.purchase_orders?.length > 0 && (
                    <span style={{ marginLeft: 4, fontSize: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 5px' }}>
                      {data.purchase_orders.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {tab === 'Details' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
                <DetailRow label="Client WO #" value={data.client_wo_number} />
                <DetailRow label="Outgoing WO #" value={data.outgoing_wo_number} />
                <DetailRow label="Engagement Type" value={ENGAGEMENT_LABELS[data.engagement_type] || data.engagement_type} />
                <DetailRow label="Status" value={<Badge variant={data.status === 'active' ? 'success' : 'neutral'}>{data.status}</Badge>} />
                <DetailRow label="Client" value={<EntityLink type="client" id={data.client_id}>{data.client_name}</EntityLink>} />
                <DetailRow label="Vendor" value={data.vendor_id ? <EntityLink type="vendor" id={data.vendor_id}>{data.vendor_name}</EntityLink> : null} />
                <DetailRow label="Contractor" value={data.contractor_id ? <EntityLink type="contractor" id={data.contractor_id}>{data.contractor_name}</EntityLink> : null} />
                <DetailRow label="Sell Rate" value={data.sell_rate ? `$${data.sell_rate}/hr` : null} />
                <DetailRow label="Buy Rate" value={data.buy_rate ? `$${data.buy_rate}/hr` : null} />
                <DetailRow label="Start Date" value={data.start_date} />
                <DetailRow label="End Date" value={data.end_date} />
                {data.notes && <DetailRow label="Notes" value={data.notes} />}
                <AuditLog tableName="work_orders" recordId={woId} />
              </div>
            )}

            {tab === 'Purchase Orders' && (
              <div>
                {(!data.purchase_orders || data.purchase_orders.length === 0) && (
                  <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0' }}>No purchase orders linked.</div>
                )}
                {data.purchase_orders?.map(po => (
                  <div key={po.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <EntityLink type="purchase_order" id={po.id} style={{ fontWeight: 700, fontSize: 13 }}>{po.po_number}</EntityLink>
                      <Badge>{po.status}</Badge>
                    </div>
                    <div style={{ color: 'var(--muted)', marginTop: 4 }}>
                      {po.period_start} – {po.period_end}{data.engagement_type === 'business_consulting' ? '' : <> · {po.sell_rate ? `$${po.sell_rate}/hr` : '—'}</>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}
